/**
 * Which clock the picked time is on.
 *
 * Moved here with the date itself: the warning belongs beside the thing it warns
 * about, and the meeting cards no longer ask for a time. Two halves, both from
 * the field this replaced —
 *
 *   - BEFORE the pick, a warning that the time is read in the SHOOT's timezone.
 *     Said first rather than after the mistake: a client in Zurich booking a
 *     Berlin shoot for 10:00 means 10:00 in Berlin.
 *   - AFTER it, the same moment in both clocks, shoot first, because that is the
 *     one it was entered in.
 *
 * Both are silent when the two zones agree, which is the ordinary case.
 */

import React from 'react'
import { Box, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { SystemMessage } from 'components/common/SystemMessage'

import { compareTimezones, dualTimezoneReadings } from '../../timezoneDisplay.helpers'

interface Props {
  /** The shoot's timezone, from the instruction catalogue. */
  timezone?: string
  /** The client's own timezone. */
  userTimezone?: string
  /** The picked moment, ISO, or undefined while the pick is incomplete. */
  scheduledAt?: string
}

export const ShootingTimezoneNotice: React.FC<Props> = ({ timezone, userTimezone, scheduledAt }) => {
  const { t } = useTranslation(['order'])

  const tz = compareTimezones(userTimezone ?? '', timezone)
  if (!tz.differs) return null

  const readings = dualTimezoneReadings(scheduledAt, tz.locationTimezone, tz.userTimezone)

  return (
    <Stack gap={0.8} data-cy="pf-revamp-schedule-timezone-notice">

      <SystemMessage
        variant="warning"
        isRawContent
        message={
          <Stack gap={0.4}>
            <span>
              {t('order:step_instruction.timezone_instruction_differs', {
                userTimezone: tz.userTimezone,
                locationTimezone: tz.locationTimezone,
              })}
            </span>
            <Box component="span" fontWeight={600}>
              {t('order:step_instruction.timezone_instruction_location')}
            </Box>
          </Stack>
        }
      />

      {readings.length > 0 &&
        <SystemMessage
          variant="system"
          isRawContent
          message={
            <Stack gap={0.2}>
              {readings.map((reading) => (
                <span key={reading.timezone}>
                  {`${reading.label} (${reading.timezone} ${t('order:step_instruction.timezone')})`}
                </span>
              ))}
            </Stack>
          }
        />
      }

    </Stack>
  )
}
